/**
 * Classifying reware-api GraphQL errors.
 *
 * `exec`/`execAdmin` attach the first `errors[]` entry to the thrown HttpError as its body. That
 * entry is flat — reware-api's errorFormatter (lib/graphql/errorFormatters/index.js) puts
 * `code`/`subcode` at the root rather than under an `extensions` wrapper.
 */

/** The fields of that payload worth branching on; the formatter emits several more. */
interface RewareErrorBody {
  code?: string;
  subcode?: number | null;
}

/** reware-api AuthError: code 'AUTH', subcode 302 — authenticated, but lacking the permission. */
const AUTH_CODE = 'AUTH';
const NOT_AUTHORIZED_SUBCODE = 302;

/**
 * True when the server refused the request for lack of permission, as opposed to any other
 * failure.
 *
 * Matched on the GraphQL error body, not the HTTP status: reware-api answers a denied query with
 * 200 and an `errors[]` entry, which `exec` reports as a 400. No 403 ever reaches the client, so
 * status alone cannot distinguish "not allowed" from "malformed query".
 *
 * Structural rather than `instanceof HttpError` so that a second copy of react-admin in the pnpm
 * tree cannot make this quietly return false — the code/subcode pair is specific enough to stand
 * on its own.
 */
export const isAuthorizationError = (error: unknown): boolean => {
  const body = (error as { body?: RewareErrorBody } | null | undefined)?.body;
  return body?.code === AUTH_CODE && body?.subcode === NOT_AUTHORIZED_SUBCODE;
};
